import { Panel } from "@/components";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { useModalStore } from "@/hooks/Modal/hooks";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import {
    EwaybillQuantityResultItem,
    getEwaybillAccountQuantityAlerts,
    syncEwaybillAccount,
} from "@/services/api/setting/ewaybillAccount";
import { WarningOutlined } from "@ant-design/icons";
import { useRequest } from "ahooks";
import { Alert, Button, Card, message, Popover, Space, Spin, Table } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import BranchQuantityThresholdSettingModal from "../../../../../components/BranchQuantityThresholdSettingModal";
import { useGlobalStore } from "@/store/hooks";
import "./index.less";

interface EwaybillAccountQuantityCardProps {}

const EwaybillAccountQuantityCard: React.FC<EwaybillAccountQuantityCardProps> = function (props) {
    const { refresh, data, refreshAsync } = useRequest(getEwaybillAccountQuantityAlerts);
    const modalStore = useModalStore();
    const globalStore = useGlobalStore();
    const history = useHistory();

    useEffect(() => {
        globalStore.ewaybillAccountStore.ensure();
    }, []);

    function buildContent() {
        let hasEnabledThreshold = false;

        try {
            Array.from(globalStore.ewaybillAccountStore.branchAccountMapping?.values())?.forEach((branchAccount) => {
                if (branchAccount.quantityAlertEnabled) {
                    hasEnabledThreshold = true;
                }
            });
        } catch {}

        if (!hasEnabledThreshold) {
            return (
                <Card
                    style={{
                        maxWidth: "80%",
                        margin: "20px auto",
                        padding: "24px",
                        borderRadius: 12,
                        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                        border: "1px solid #f0f0f0",
                    }}
                    styles={{ body: { padding: 0 } }}
                >
                    <div className="row">
                        <div className="left-section">
                            <div className="circle">1</div>
                            <span className="text">设置单号的余额预警</span>
                        </div>
                        <Button
                            type="primary"
                            ghost
                            className="button"
                            onClick={async () => {
                                modalStore.openModal(BranchQuantityThresholdSettingModal, {
                                    onSuccess() {
                                        refresh();
                                    },
                                    threshold: data?.threshold,
                                });
                            }}
                        >
                            设置余额预警
                        </Button>
                    </div>

                    <div className="divider" />

                    <div className="row">
                        <div className="left-section">
                            <div className="circle">2</div>
                            <span className="text">对需要预警的网点开启预警</span>
                        </div>
                        <Button
                            type="primary"
                            className="button"
                            onClick={() => {
                                history.push("/setting/ewaybillAccount");
                            }}
                        >
                            去开启
                        </Button>
                    </div>
                </Card>
            );
        }

        const dataSource: (EwaybillQuantityResultItem & { alert?: boolean })[] = [
            ...(data?.alerts || []).map((it) => ({ ...it, alert: true })),
            ...(data?.noAlerts || []),
        ];

        return (
            <Card variant="borderless">
                <div>
                    {data?.alerts?.length ? (
                        <Alert
                            title={
                                <span>
                                    <WarningOutlined style={{ color: "#fa8c16", marginRight: "8px" }} />
                                    {data.alerts.length}个快递网点余额不足, 请尽快联系对应的快递网点充值,
                                    <Link to="/setting/ewaybillAccount">点击查看电子面单详情</Link>
                                </span>
                            }
                            type="warning"
                            showIcon={false}
                            style={{
                                backgroundColor: "#fffbe6",
                                border: "1px solid #ffe58f",
                                marginBottom: "16px",
                                borderRadius: "6px",
                            }}
                        />
                    ) : null}

                    <div style={{ textAlign: "right", margin: "4px 0" }}>
                        <Space>
                            <AutoLoadingButton
                                type="link"
                                onClick={async () => {
                                    modalStore.openModal(BranchQuantityThresholdSettingModal, {
                                        onSuccess() {
                                            refresh();
                                        },
                                        threshold: data?.threshold,
                                    });
                                }}
                            >
                                设置余额预警
                            </AutoLoadingButton>

                            <AutoLoadingButton
                                type="link"
                                onClick={async () => {
                                    await syncEwaybillAccount();
                                    message.success("更新成功");

                                    await refreshAsync();
                                }}
                            >
                                更新余额
                            </AutoLoadingButton>
                        </Space>
                    </div>

                    <div className="panel-overview-table" style={{ border: "1px solid #ddd", borderRadius: "5px" }}>
                        <Table
                            rowKey="ewaybillBranchAccountId"
                            columns={[
                                {
                                    title: "快递网点",
                                    width: 200,
                                    render(_, item) {
                                        return (
                                            <div>
                                                <div>
                                                    <span>{LogisticsCompanyNameMap[item?.logisticsCompany]}</span>
                                                    <span>-</span>
                                                    <span>{item?.branchName}</span>
                                                </div>
                                                <div className="text-gray text-small">{item?.ewaybillAccountName}</div>
                                            </div>
                                        );
                                    },
                                },
                                {
                                    title: "余额",
                                    width: 50,
                                    render(_, item) {
                                        if (item.alert) {
                                            return (
                                                <Popover title="单号余额少于设置的预警值,请及时充值">
                                                    <span className="error">{item?.quantity}</span>
                                                </Popover>
                                            );
                                        }

                                        return <span>{item?.quantity}</span>;
                                    },
                                },
                            ]}
                            dataSource={dataSource}
                            pagination={false}
                            size="small"
                            scroll={{ y: 300 }}
                        />
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Panel title="电子面单余额预警">
            <div style={{ minHeight: "230px" }} className="ewaybill-account-quantity-card">
                {buildContent()}
            </div>
        </Panel>
    );
};

export default observer(EwaybillAccountQuantityCard);
